import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LayersOutlined, DeleteOutlineOutlined, TouchAppOutlined,
  DragIndicatorOutlined, WarningAmberOutlined,
  ExpandMoreOutlined, ExpandLessOutlined, ImageOutlined,
} from '@mui/icons-material';
import { SHAPE_TYPES } from '../constants';

const CAROUSEL_COLOR = '#c2410c';
const isElement = h => ['stream', 'livestream-chat', 'exhibitor-grid'].includes(h.hotspotType || h.elementType);

/**
 * Layers panel.
 *
 * Regular lobbies: flat drag-to-reorder list of hotspots.
 *
 * Carousel lobbies: slides as top-level collapsible rows, with each slide's
 * hotspots/elements shown as indented sub-rows. No items outside slides.
 * The pinned "Lobby Carousel" row sits below all slides with a delete that
 * shows a confirmation modal.
 */
export function LayersPanel({
  hotspots,
  selectedId,
  onSelect,
  onDelete,
  onReorder,
  lobbyType,
  onRemoveCarousel,
  // Carousel extras
  slides = [],
  activeSlideIndex = 0,
  onSelectSlide,
  onDeleteSlide,
  onSelectSlideHotspot,
  onDeleteSlideHotspot,
}) {
  const [dragId, setDragId]         = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // Sub-row drag state for reordering within a slide
  const [subDragId, setSubDragId]     = useState(null);  // { slideIdx, hotspotId }
  const [subDragOver, setSubDragOver] = useState(null);  // { slideIdx, hotspotId }
  // Which slide rows are expanded (all start collapsed except the active one)
  const [expanded, setExpanded] = useState(() => ({ [activeSlideIndex]: true }));
  const dragIndex = useRef(null);

  const isCarousel = lobbyType === 'carousel';

  // Auto-expand whichever slide is active
  React.useEffect(() => {
    setExpanded(prev => ({ ...prev, [activeSlideIndex]: true }));
  }, [activeSlideIndex]);

  // ── Non-carousel: flat sorted list ────────────────────────────────────────
  const sorted = [...hotspots].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));

  const handleDragStart = (e, h, idx) => {
    setDragId(h.id);
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.style.cssText = 'position:fixed;left:-9999px;top:0';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragOver = (e, h) => { e.preventDefault(); if (h.id !== dragId) setDragOverId(h.id); };

  const handleDrop = (e, targetH) => {
    e.preventDefault();
    if (!dragId || targetH.id === dragId) { reset(); return; }
    const without = sorted.filter(h => h.id !== dragId);
    const targetIdx = without.findIndex(h => h.id === targetH.id);
    const dragged = sorted.find(h => h.id === dragId);
    const newOrder = [...without.slice(0, targetIdx), dragged, ...without.slice(targetIdx)];
    const total = newOrder.length;
    onReorder?.(newOrder.map((h, i) => ({ ...h, order: total - 1 - i })));
    reset();
  };

  const handleDragEnd = () => reset();
  const reset = () => { setDragId(null); setDragOverId(null); dragIndex.current = null; };

  const toggleExpanded = (idx, e) => {
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // ── Row renderer for a single hotspot/element ────────────────────────────
  // ── Row renderer ──────────────────────────────────────────────────────────
  const renderHotspotRow = ({ h, isSelected, onRowSelect, onRowDelete, indented = false, onDragStart, onDragOverRow, onDropRow, onDragEndRow, isDragging: isRowDragging, isDragOver: isRowDragOver }) => {
    const shape = SHAPE_TYPES[h.hotspotType] || SHAPE_TYPES[h.elementType] || SHAPE_TYPES[h.shape] || SHAPE_TYPES.polygon;
    const color = shape?.color || '#9ea8b3';
    const elem  = isElement(h);
    return (
      <Box
        key={h.id}
        draggable={!!onDragStart}
        onDragStart={onDragStart}
        onDragOver={onDragOverRow}
        onDrop={onDropRow}
        onDragEnd={onDragEndRow}
        onClick={e => { e.stopPropagation(); onRowSelect(); }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          pl: indented ? 3 : 1, pr: 1, py: 0.7,
          ml: indented ? 1 : 0.5, mr: 0.5, mb: 0.2,
          borderRadius: 1.5, cursor: onDragStart ? 'grab' : 'pointer', userSelect: 'none',
          background: isSelected ? alpha(color, 0.10) : isRowDragOver ? alpha(color, 0.06) : 'transparent',
          border: `1px solid ${isSelected ? alpha(color, 0.25) : isRowDragOver ? alpha(color, 0.3) : 'transparent'}`,
          opacity: isRowDragging ? 0.35 : 1,
          transition: 'all 0.1s',
          '&:hover': { background: isSelected ? alpha(color, 0.12) : alpha('#000', 0.03) },
          '&:hover .layer-del-btn': { opacity: 1 },
          '&:active': onDragStart ? { cursor: 'grabbing' } : {},
        }}
      >
        {onDragStart && <DragIndicatorOutlined sx={{ fontSize: 11, color: '#c8d0dc', flexShrink: 0 }} />}
        <Box sx={{ color, display: 'flex', flexShrink: 0 }}>{shape?.icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: isSelected ? 700 : 400, color: isSelected ? color : '#3c4555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {h.label || shape?.label || 'Layer'}
            </Typography>
            {elem && (
              <Box sx={{ px: 0.5, py: 0.1, borderRadius: '3px', background: alpha(color, 0.1), fontSize: '0.52rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>el</Box>
            )}
          </Box>
          {!h.isActive && <Typography sx={{ fontSize: '0.58rem', color: '#f59e0b', fontWeight: 600 }}>Hidden</Typography>}
        </Box>
        <Box className="layer-del-btn" onClick={e => { e.stopPropagation(); onRowDelete(); }}
          sx={{ opacity: 0, transition: 'opacity 0.1s', color: '#d32f2f', display: 'flex', cursor: 'pointer', borderRadius: 0.75, p: 0.25, '&:hover': { background: alpha('#d32f2f', 0.1) }, flexShrink: 0 }}>
          <DeleteOutlineOutlined sx={{ fontSize: 11 }} />
        </Box>
      </Box>
    );
  };


  return (
    <Box
      onClick={e => e.stopPropagation()}
      sx={{ width: 220, flexShrink: 0, borderLeft: '1px solid #e8edf2', background: '#fafbfc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', gap: 1 }}>
        <LayersOutlined sx={{ fontSize: 15, color: '#9ea8b3' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Layers
        </Typography>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>

        {/* ── Carousel mode: slides tree ── */}
        {isCarousel ? (
          <>
            {slides.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <TouchAppOutlined sx={{ fontSize: 28, color: '#d4dbe6', mb: 1 }} />
                <Typography sx={{ fontSize: '0.72rem', color: '#b0bac8' }}>No slides yet</Typography>
              </Box>
            )}

            {slides.map((slide, idx) => {
              const isActiveSlide  = idx === activeSlideIndex;
              const isOpen         = !!expanded[idx];
              const slideHotspots  = [...(slide.hotspots || []), ...(slide.elements || [])];
              // Show slide highlight only when the canvas itself is selected (not a child, not null)
              const childSelected      = isActiveSlide && !!selectedId && selectedId !== 'canvas' && slideHotspots.some(h => h.id === selectedId);
              const showSlideHighlight = isActiveSlide && selectedId === 'canvas';

              return (
                <Box key={slide.id} sx={{ mb: 0.5 }}>
                  {/* Slide header row */}
                  <Box
                    onClick={() => { onSelectSlide?.(idx); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 1, py: 0.8, mx: 0.5,
                      borderRadius: 1.5, cursor: 'pointer', userSelect: 'none',
                      background: showSlideHighlight ? alpha(CAROUSEL_COLOR, 0.07) : 'transparent',
                      border: `1px solid ${showSlideHighlight ? alpha(CAROUSEL_COLOR, 0.25) : 'transparent'}`,
                      transition: 'all 0.1s',
                      '&:hover': { background: showSlideHighlight ? alpha(CAROUSEL_COLOR, 0.09) : alpha('#000', 0.04) },
                      '&:hover .slide-del-btn': { opacity: 1 },
                    }}
                  >
                    {/* Thumbnail */}
                    <Box sx={{ width: 28, height: 17, borderRadius: '3px', flexShrink: 0, overflow: 'hidden', border: `1px solid ${showSlideHighlight ? alpha(CAROUSEL_COLOR, 0.3) : '#e4e9f0'}`, background: slide.bgUrl ? 'transparent' : '#f0f4f8', position: 'relative' }}>
                      {slide.bgUrl
                        ? <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${slide.bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                        : <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ImageOutlined sx={{ fontSize: 9, color: '#c4cdd8' }} /></Box>
                      }
                      <Box sx={{ position: 'absolute', top: 0, left: 1, fontSize: '0.38rem', fontWeight: 700, color: '#fff', lineHeight: 1, textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>{idx + 1}</Box>
                    </Box>

                    {/* Label */}
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: showSlideHighlight ? 700 : 500, color: showSlideHighlight ? CAROUSEL_COLOR : '#3c4555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {slide.label || `Slide ${idx + 1}`}
                    </Typography>

                    {/* Child count badge */}
                    {slideHotspots.length > 0 && (
                      <Box sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#9ea8b3', mr: 0.25 }}>{slideHotspots.length}</Box>
                    )}

                    {/* Expand toggle */}
                    <Box onClick={e => toggleExpanded(idx, e)} sx={{ color: '#c4cdd8', display: 'flex', flexShrink: 0, '&:hover': { color: CAROUSEL_COLOR } }}>
                      {isOpen
                        ? <ExpandLessOutlined sx={{ fontSize: 14 }} />
                        : <ExpandMoreOutlined sx={{ fontSize: 14 }} />
                      }
                    </Box>

                    {/* Delete slide — always shown; deleting last converts to normal lobby */}
                    <Box
                      className="slide-del-btn"
                      onClick={e => { e.stopPropagation(); onDeleteSlide?.(idx); }}
                      sx={{ opacity: 0, transition: 'opacity 0.1s', color: '#d32f2f', display: 'flex', cursor: 'pointer', borderRadius: 0.75, p: 0.25, '&:hover': { background: alpha('#d32f2f', 0.1) }, flexShrink: 0 }}
                    >
                      <DeleteOutlineOutlined sx={{ fontSize: 11 }} />
                    </Box>
                  </Box>

                  {/* Sub-rows: slide hotspots/elements sorted top=highest z-index */}
                  {isOpen && slideHotspots.length > 0 && (() => {
                    // Sort descending: top of list = highest order = highest z-index
                    const sorted = [...slideHotspots].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));

                    const handleSubDragStart = (e, h) => {
                      setSubDragId({ slideIdx: idx, hotspotId: h.id });
                      e.dataTransfer.effectAllowed = 'move';
                      const ghost = document.createElement('div');
                      ghost.style.cssText = 'position:fixed;left:-9999px';
                      document.body.appendChild(ghost);
                      e.dataTransfer.setDragImage(ghost, 0, 0);
                      setTimeout(() => document.body.removeChild(ghost), 0);
                    };

                    const handleSubDrop = (e, targetH) => {
                      e.preventDefault(); e.stopPropagation();
                      if (!subDragId || subDragId.slideIdx !== idx || subDragId.hotspotId === targetH.id) {
                        setSubDragId(null); setSubDragOver(null); return;
                      }
                      // Reorder: rebuild array with new positions, then assign order values (top=max)
                      const without = sorted.filter(h => h.id !== subDragId.hotspotId);
                      const targetPos = without.findIndex(h => h.id === targetH.id);
                      const dragged = sorted.find(h => h.id === subDragId.hotspotId);
                      const newSorted = [...without.slice(0, targetPos), dragged, ...without.slice(targetPos)];
                      const total = newSorted.length;
                      const reordered = newSorted.map((h, i) => ({ ...h, order: total - 1 - i }));
                      onDeleteSlideHotspot?.(idx, '__reorder__', reordered);
                      setSubDragId(null); setSubDragOver(null);
                    };

                    return (
                      <Box sx={{ position: 'relative' }}>
                        <Box sx={{ position: 'absolute', left: 13, top: 0, bottom: 4, width: '1px', background: '#e8edf2' }} />
                        {sorted.map(h =>
                          renderHotspotRow({
                            h,
                            isSelected: selectedId === h.id,
                            onRowSelect: () => { onSelectSlide?.(idx); onSelectSlideHotspot?.(h.id, idx); },
                            onRowDelete: () => onDeleteSlideHotspot?.(idx, h.id),
                            indented: true,
                            onDragStart: e => { e.stopPropagation(); handleSubDragStart(e, h); },
                            onDragOverRow: e => { e.preventDefault(); e.stopPropagation(); setSubDragOver({ slideIdx: idx, hotspotId: h.id }); },
                            onDropRow: e => handleSubDrop(e, h),
                            onDragEndRow: () => { setSubDragId(null); setSubDragOver(null); },
                            isDragging: subDragId?.slideIdx === idx && subDragId?.hotspotId === h.id,
                            isDragOver: subDragOver?.slideIdx === idx && subDragOver?.hotspotId === h.id,
                          })
                        )}
                      </Box>
                    );
                  })()}

                  {isOpen && slideHotspots.length === 0 && (
                    <Typography sx={{ fontSize: '0.62rem', color: '#c8d0dc', pl: 3.5, py: 0.5 }}>Empty slide</Typography>
                  )}
                </Box>
              );
            })}

          </>
        ) : (
          <>
            {/* ── Regular mode: flat list ── */}
            {sorted.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <TouchAppOutlined sx={{ fontSize: 28, color: '#d4dbe6', mb: 1 }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#b0bac8' }}>No layers yet</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#c8d0dc', mt: 0.5 }}>Drag elements onto the canvas</Typography>
              </Box>
            )}

            {sorted.map((h, idx) => {
              const shape = SHAPE_TYPES[h.hotspotType] || SHAPE_TYPES[h.shape] || SHAPE_TYPES.polygon;
              const color = shape?.color || '#9ea8b3';
              const elem  = isElement(h);
              const isSelected = selectedId === h.id;
              const isDragging = dragId === h.id;
              const isOver     = dragOverId === h.id;

              return (
                <Box
                  key={h.id}
                  draggable
                  onDragStart={e => handleDragStart(e, h, idx)}
                  onDragOver={e => handleDragOver(e, h)}
                  onDrop={e => handleDrop(e, h)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onSelect(h.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 1, py: 0.85, mx: 0.5, mb: 0.25,
                    borderRadius: 1.5, cursor: 'grab', userSelect: 'none',
                    background: isSelected ? alpha(color, 0.10) : isOver ? alpha(color, 0.06) : 'transparent',
                    border: `1px solid ${isSelected ? alpha(color, 0.25) : isOver ? alpha(color, 0.35) : 'transparent'}`,
                    opacity: isDragging ? 0.35 : 1,
                    borderTop: isOver ? `2px solid ${color}` : undefined,
                    transition: 'all 0.1s',
                    '&:hover': { background: isSelected ? alpha(color, 0.12) : alpha('#000', 0.04) },
                    '&:hover .layer-del-btn': { opacity: 1 },
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <DragIndicatorOutlined sx={{ fontSize: 13, color: '#c8d0dc', flexShrink: 0 }} />
                  <Box sx={{ color, display: 'flex', flexShrink: 0 }}>{shape?.icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? color : '#3c4555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {h.label || shape?.label}
                      </Typography>
                      {elem && (
                        <Box sx={{ px: 0.6, py: 0.1, borderRadius: '4px', background: alpha(color, 0.12), fontSize: '0.55rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
                          element
                        </Box>
                      )}
                    </Box>
                    {!h.isActive && <Typography sx={{ fontSize: '0.62rem', color: '#f59e0b', fontWeight: 600 }}>Hidden</Typography>}
                  </Box>
                  <Box
                    className="layer-del-btn"
                    onClick={e => { e.stopPropagation(); onDelete(h.id); }}
                    sx={{ opacity: 0, transition: 'opacity 0.1s', color: '#d32f2f', display: 'flex', cursor: 'pointer', borderRadius: 0.75, p: 0.25, '&:hover': { background: alpha('#d32f2f', 0.1) }, flexShrink: 0 }}
                  >
                    <DeleteOutlineOutlined sx={{ fontSize: 12 }} />
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {/* Confirm remove carousel modal */}
      {confirmRemove && (
        <Box onClick={() => setConfirmRemove(false)}
          sx={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box onClick={e => e.stopPropagation()}
            sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.22)', p: 3, width: 340, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: '#fff5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <WarningAmberOutlined sx={{ fontSize: 22, color: '#dc2626' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>Remove Exhibitor Carousel?</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: '#5a6474', lineHeight: 1.6 }}>
              Removing the exhibitor carousel will <strong>permanently delete all slide contents</strong> including backgrounds, hotspots, and elements across all slides. This cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Box onClick={() => setConfirmRemove(false)}
                sx={{ flex: 1, py: 0.9, borderRadius: '8px', border: '1px solid #e4e9f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#5a6474', fontSize: '0.78rem', fontWeight: 600, '&:hover': { borderColor: '#c8d4df', background: '#f8f9fb' } }}>
                Cancel
              </Box>
              <Box onClick={() => { setConfirmRemove(false); onRemoveCarousel?.(); }}
                sx={{ flex: 1.4, py: 0.9, borderRadius: '8px', background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: '0.78rem', fontWeight: 700, '&:hover': { background: '#b91c1c' } }}>
                Remove Carousel
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}